import React, { useState, useEffect } from 'react';

export type InsightSection = 
  | 'overview'
  | 'platform-coverage'
  | 'feature-coverage'
  | 'untested'
  | 'needs-test-case'
  | 'bug-rates'
  | 'component-health'
  | 'bug-prone-components'
  | 'bug-prone-features'
  | 'bug-discovery-rate'
  | 'bug-tracker'
  | 'trends'
  | 'test-plan-completion'
  | 'milestone-completion'
  | 'platform-map'
  | 'tester-performance';

interface InsightOption {
  id: InsightSection;
  label: string;
  category: 'overview' | 'coverage' | 'quality' | 'planning' | 'performance';
  requiresRole?: 'admin' | 'manager';
}

interface PDFReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (selectedSections: InsightSection[], milestone: string) => void;
  availableMilestones: string[];
  userRole: string;
}

const insightOptions: InsightOption[] = [
  // Overview
  { id: 'overview', label: 'Overview Dashboard', category: 'overview' },
  
  // Coverage
  { id: 'platform-coverage', label: 'Platform Coverage', category: 'coverage' },
  { id: 'feature-coverage', label: 'Feature Coverage', category: 'coverage' },
  { id: 'untested', label: 'Untested Components', category: 'coverage' },
  { id: 'needs-test-case', label: 'Components Needing Test Cases', category: 'coverage' },
  
  // Quality
  { id: 'bug-rates', label: 'Bug Rates', category: 'quality' },
  { id: 'component-health', label: 'Component Health', category: 'quality' },
  { id: 'bug-prone-components', label: 'Most Bug-Prone Components', category: 'quality' },
  { id: 'bug-prone-features', label: 'Most Bug-Prone Features', category: 'quality' },
  { id: 'bug-discovery-rate', label: 'Bug Discovery Rate', category: 'quality' },
  { id: 'bug-tracker', label: 'Bug Tracker', category: 'quality' },
  { id: 'trends', label: 'Testing Trends', category: 'quality' },
  
  // Planning
  { id: 'test-plan-completion', label: 'Test Plan Completion', category: 'planning' },
  { id: 'milestone-completion', label: 'Milestone Completion', category: 'planning' },
  { id: 'platform-map', label: 'Platform Testing Map', category: 'planning' },
  
  // Performance (requires admin/manager)
  { id: 'tester-performance', label: 'Tester Performance', category: 'performance', requiresRole: 'admin' },
];

const PDFReportModal: React.FC<PDFReportModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  availableMilestones,
  userRole,
}) => {
  const [selectedMilestone, setSelectedMilestone] = useState<string>('all-time');
  const [selectedSections, setSelectedSections] = useState<Set<InsightSection>>(new Set<InsightSection>(['overview' as InsightSection]));
  const [sectionOrder, setSectionOrder] = useState<InsightSection[]>(['overview' as InsightSection]);

  useEffect(() => {
    if (isOpen) {
      // Reset selections when modal opens
      setSelectedSections(new Set<InsightSection>(['overview' as InsightSection]));
      setSectionOrder(['overview' as InsightSection]);
      setSelectedMilestone('all-time');
      
      // Prevent background scrolling
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable background scrolling
      document.body.style.overflow = '';
    }
    
    // Cleanup function to restore scrolling if component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Update section order when selections change
  useEffect(() => {
    const currentOrder = sectionOrder.filter(section => selectedSections.has(section));
    const newSections = Array.from(selectedSections).filter(section => !currentOrder.includes(section));
    setSectionOrder([...currentOrder, ...newSections]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSections]);

  // Filter options based on user role
  const visibleOptions = insightOptions.filter(option => {
    if (option.requiresRole) {
      return userRole === 'admin' || userRole === 'manager';
    }
    return true;
  });

  // Group options by category
  const optionsByCategory = visibleOptions.reduce((acc, option) => {
    if (!acc[option.category]) {
      acc[option.category] = [];
    }
    acc[option.category].push(option);
    return acc;
  }, {} as Record<string, InsightOption[]>);

  const categoryLabels: Record<string, string> = {
    overview: '📊 Overview',
    coverage: '✅ Coverage',
    quality: '🐛 Quality',
    planning: '📋 Planning',
    performance: '⚡ Performance',
  };

  const toggleSection = (sectionId: InsightSection) => {
    const newSelected = new Set(selectedSections);
    if (newSelected.has(sectionId)) {
      newSelected.delete(sectionId);
      // Remove from order
      setSectionOrder(prev => prev.filter(id => id !== sectionId));
    } else {
      newSelected.add(sectionId);
      // Add to end of order
      setSectionOrder(prev => [...prev, sectionId]);
    }
    setSelectedSections(newSelected);
  };

  const moveSectionUp = (index: number) => {
    if (index === 0) return;
    const newOrder = [...sectionOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setSectionOrder(newOrder);
  };

  const moveSectionDown = (index: number) => {
    if (index === sectionOrder.length - 1) return;
    const newOrder = [...sectionOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setSectionOrder(newOrder);
  };


  const selectAllInCategory = (category: string) => {
    const newSelected = new Set(selectedSections);
    const categoryOptions = optionsByCategory[category] || [];
    const newSections: InsightSection[] = [];
    categoryOptions.forEach(option => {
      if (!newSelected.has(option.id)) {
        newSelected.add(option.id);
        newSections.push(option.id);
      }
    });
    setSelectedSections(newSelected);
    setSectionOrder(prev => [...prev, ...newSections]);
  };

  const deselectAllInCategory = (category: string) => {
    const newSelected = new Set(selectedSections);
    const categoryOptions = optionsByCategory[category] || [];
    const removedSections: InsightSection[] = [];
    categoryOptions.forEach(option => {
      if (newSelected.has(option.id)) {
        newSelected.delete(option.id);
        removedSections.push(option.id);
      }
    });
    setSelectedSections(newSelected);
    setSectionOrder(prev => prev.filter(id => !removedSections.includes(id)));
  };

  const selectAll = () => {
    const newSelected = new Set(visibleOptions.map(opt => opt.id));
    const newSections = visibleOptions
      .map(opt => opt.id)
      .filter(id => !selectedSections.has(id));
    setSelectedSections(newSelected);
    setSectionOrder(prev => [...prev, ...newSections]);
  };

  const deselectAll = () => {
    setSelectedSections(new Set());
    setSectionOrder([]);
  };

  const handleGenerate = () => {
    if (selectedSections.size === 0) {
      alert('Please select at least one insight section to include in the report.');
      return;
    }
    // Use the ordered sections, filtering to only include selected ones
    const orderedSelected = sectionOrder.filter(section => selectedSections.has(section));
    onGenerate(orderedSelected, selectedMilestone);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '2rem',
      }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative',
          backgroundColor: 'var(--surface)',
        }}
      >
        <div
          style={{
            padding: '1.5rem',
            borderBottom: '1px solid var(--grid-line)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            backgroundColor: 'var(--surface)',
            zIndex: 10,
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
            Generate PDF Report
          </h2>
          <button
            className="btn btn-ghost btn-icon"
            onClick={onClose}
            style={{ fontSize: '1.5rem', padding: '0.5rem' }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {/* Milestone Filter */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--text-primary)' }}>
              Filter by Milestone:
            </label>
            <select
              value={selectedMilestone}
              onChange={(e) => setSelectedMilestone(e.target.value)}
              className="insights-select"
              style={{ width: '100%', maxWidth: '300px' }}
            >
              <option value="all-time">All Time</option>
              {availableMilestones.map(milestone => (
                <option key={milestone} value={milestone}>
                  {milestone}
                </option>
              ))}
            </select>
          </div>

          {/* Selection Controls */}
          <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              onClick={selectAll}
              style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
            >
              Select All
            </button>
            <button
              className="btn btn-secondary"
              onClick={deselectAll}
              style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
            >
              Deselect All
            </button>
          </div>

          {/* Insight Sections by Category */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '1rem', fontWeight: '600', color: 'var(--text-primary)' }}>
              Select Insights to Include:
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {Object.entries(optionsByCategory).map(([category, options]) => (
                <div
                  key={category}
                  style={{
                    border: '1px solid var(--grid-line)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      padding: '0.75rem 1rem',
                      backgroundColor: 'var(--surface-secondary)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                        {categoryLabels[category]}
                      </span>
                      <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        ({options.filter(opt => selectedSections.has(opt.id)).length} / {options.length} selected)
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="btn btn-ghost"
                        onClick={() => selectAllInCategory(category)}
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      >
                        Select All
                      </button>
                      <button
                        className="btn btn-ghost"
                        onClick={() => deselectAllInCategory(category)}
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div style={{ padding: '0.5rem' }}>
                    {options.map(option => (
                      <label
                        key={option.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--surface-secondary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedSections.has(option.id)}
                          onChange={() => toggleSection(option.id)}
                          style={{
                            marginRight: '0.75rem',
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer',
                          }}
                        />
                        <span style={{ color: 'var(--text-primary)', flex: 1 }}>
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Selected Sections Order */}
          {sectionOrder.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                Report Order (drag to reorder):
              </label>
              <div
                style={{
                  border: '1px solid var(--grid-line)',
                  borderRadius: '8px',
                  backgroundColor: 'var(--surface-secondary)',
                  padding: '0.5rem',
                }}
              >
                {sectionOrder.map((sectionId, index) => {
                  const option = visibleOptions.find(opt => opt.id === sectionId);
                  if (!option) return null;
                  
                  return (
                    <div
                      key={sectionId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.75rem',
                        marginBottom: index < sectionOrder.length - 1 ? '0.5rem' : 0,
                        backgroundColor: 'var(--surface)',
                        borderRadius: '4px',
                        border: '1px solid var(--grid-line)',
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginRight: '0.75rem' }}>
                        <button
                          className="btn btn-ghost btn-icon"
                          onClick={() => moveSectionUp(index)}
                          disabled={index === 0}
                          style={{
                            padding: '0.25rem',
                            fontSize: '0.75rem',
                            opacity: index === 0 ? 0.3 : 1,
                            cursor: index === 0 ? 'not-allowed' : 'pointer',
                            minWidth: '24px',
                            height: '24px',
                          }}
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          className="btn btn-ghost btn-icon"
                          onClick={() => moveSectionDown(index)}
                          disabled={index === sectionOrder.length - 1}
                          style={{
                            padding: '0.25rem',
                            fontSize: '0.75rem',
                            opacity: index === sectionOrder.length - 1 ? 0.3 : 1,
                            cursor: index === sectionOrder.length - 1 ? 'not-allowed' : 'pointer',
                            minWidth: '24px',
                            height: '24px',
                          }}
                          title="Move down"
                        >
                          ↓
                        </button>
                      </div>
                      <span style={{ flex: 1, color: 'var(--text-primary)', fontWeight: '500' }}>
                        {index + 1}. {option.label}
                      </span>
                      <span
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--text-secondary)',
                          padding: '0.25rem 0.5rem',
                          backgroundColor: 'var(--surface-secondary)',
                          borderRadius: '4px',
                        }}
                      >
                        {categoryLabels[option.category]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Selected Count */}
          <div style={{ marginBottom: '1.5rem', padding: '0.75rem', backgroundColor: 'var(--surface-secondary)', borderRadius: '4px' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              {selectedSections.size} section{selectedSections.size !== 1 ? 's' : ''} selected
            </span>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={selectedSections.size === 0}
            >
              Generate PDF Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFReportModal;

